<!-- @ts-nocheck -->
<template>
  <div >
    <q-select
      v-if="isComponentEnabled"
      v-model="localvalue"
      filled
      :multiple="true"
      dense
      input-debounce="0"
      :options="curOptions"
      :disabled="disabled"
      :disable="disabled"
      :readonly="disabled"
      :label="getLabel()"
      style="padding-bottom: 0"
      input-style="height: 100px;"
    >
      <template v-slot:prepend>
        <dtpIcon name="filter_alt" @click.stop.prevent />
      </template>
      <template v-slot:option="scope">
        <dtpItem v-bind="scope.itemProps" @click="userInteracted">
          <dtpItemSection>
            <dtpItemLabel>
              {{ scope.opt.label }}<dtpIcon v-if="scope.opt.hidden"
                                            name="mdi-eye-off"
              /></dtpItemLabel>
            <dtpItemLabel v-if="scope.opt.sublabel" caption>{{ scope.opt.sublabel }}</dtpItemLabel>
          </dtpItemSection>
        </dtpItem>
      </template>
      <template v-slot:append>
        <dtpIcon
          v-if="localvalue && localvalue.length > 0"
          name="cancel"
          @click="
            localvalue = [];
            userInteracted();
          "
          icon-class="cursor-pointer q-field__focusable-action"
        />
      </template>
    </q-select>
    <CuiMultiSelect
      v-else
      v-model="localvalue"
      :label="getLabel()"
      :items="safeOptions"
      :description="getLabel() || 'Select options'"
      :wcag-label="getLabel() || 'Multi-select'"
      :disabled="disabled"
      @update:modelValue="handleCuiUpdate"
    />
  </div>
</template>
<script setup lang="ts">
// @ts-nocheck
import { onMounted, ref, watch, computed } from "vue";
import { QSelect } from "quasar";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { CuiMultiSelect } from "@ist/commonui-components";
const { isEnabled } = useFeatureFlags();
const isComponentEnabled = computed(() => isEnabled("IstMultipleSelect"));

export type optiontype = {
  label: string;
  value: any;
  grouptext?: string;
  sublabel?: string;
  hidden?: boolean;
};

defineOptions({ name: "IstMultipleSelect" });

const props = withDefaults(
  defineProps<{
    modelValue?: any;
    disabled?: boolean;
    label?: string;
    options?: Array<optiontype>;
    dense?: boolean;
    inputStyle?: string;
  }>(),
  {
    modelValue: null,
    disabled: false,
    label: undefined,
    options: () => [],
    dense: false,
    inputStyle: undefined
  }
);
const emit = defineEmits<{
  (e: "input", val: any): void;
  (e: "change", val: any): void;
  (e: "update:modelValue", val: any): void;
  (e: "userInteracted"): void;
}>();

const ldense = ref(false);
const curOptions = ref<Array<optiontype>>([]);
const localvalue = ref<any>(null);

// v-model is handled by watchers; no explicit update method needed

const userInteracted = () => emit("userInteracted");

// Handle CuiMultiSelect value updates and convert back to original format
const handleCuiUpdate = (newValue: any) => {
  if (!newValue || !Array.isArray(newValue)) {
    localvalue.value = [];
    return;
  }
  // Convert {id, value} back to original values
  localvalue.value = newValue.map((item: any) => item.id);
  userInteracted();
};

watch(
  () => props.modelValue,
  (val: any) => {
    localvalue.value = val || undefined;
  },
  { immediate: true }
);

// Emit updates when the local selection changes (user interaction)
watch(
  localvalue,
  (val: any) => {
    emit("input", val);
    emit("change", val);
    emit("update:modelValue", val);
  },
  { deep: true }
);

watch(
  () => props.dense,
  (val: any) => {
    ldense.value = val;
  },
  { immediate: true }
);

watch(
  () => props.options,
  (val: any) => {
    curOptions.value = val;
    let oldtxt = "";
    for (const element of curOptions.value) {
      const s = element.grouptext ? element.grouptext : "";
      if (s !== "" && oldtxt != s) oldtxt = s;
      else element.grouptext = undefined;
    }
  },
  { immediate: true }
);

onMounted(() => {
  localvalue.value = props.modelValue;
  curOptions.value = props.options as any;
});

const getLabel = () => {
  if (!props.label) return;
  const s: string = props.label.charAt(0).toUpperCase() + props.label.substring(1).toLowerCase();
  return s;
};

// Transform options to CuiMultiSelect format: {id, value} instead of {label, value}
const safeOptions = computed(() => {
  if (!curOptions.value) return [];
  return curOptions.value.map((opt, index) => ({
    id: opt.value ?? index,
    value: opt.label
  }));
});

// Transform localvalue to CuiMultiSelect format for v-model
const cuiSelectedItems = computed({
  get: () => {
    if (!localvalue.value || !Array.isArray(localvalue.value)) return [];
    return safeOptions.value.filter(opt =>
      localvalue.value.includes(opt.id)
    );
  },
  set: (newValue: any) => {
    handleCuiUpdate(newValue);
  }
});
</script>
<style scoped>
.showerror {
  padding-bottom: 20px !important;
}
:deep(.q-field--auto-height.q-field--dense .q-field__control) {
  min-height: 60px;
  height: auto;
}

:deep(.q-field--dense .q-field__append) {
  padding-top: 22px;
}
</style>
